import type { ReactNode } from "react";

/** The Activity card on Home (the home-and-customers round, 2026-11-03; the founder's variation A, "Are we keeping up?"): the open tickets
 *  now in large, a sentence about the period's arrivals against its answered tickets, two bars per day (`FlowBars`) with their legend and the
 *  period's dates, and a grid of small figures, value over label, in equal cells (five to a row on a wide card, three in a narrow one, two
 *  on a phone) so nothing wraps mid-fact. Props in: the screen writes every word and number. The bars are two greys of the data ink; never
 *  the AI hue. The copy-and-charts round: answered replaces resolved, the bars are the Reports bars' columns, the labels are short. */

export type FlowBar = { key: string; /** under the column: "Oct 28" */ label: string; /** the kit's bubble, written by the screen: "Oct 28 · 44 arrived · 19 answered" */ tip: string; arrived: number; answered: number };

/** the most day labels under the bars; more columns name every second (or third …) one, the last always named */
const MOST_LABELS = 7;

/** Two bars per day (or week) in the Reports bars' style (the copy-and-charts round, part five): equal HTML columns (`ob-bars`, at most 44 px
 *  wide, rounded tops, the hover dimming), each holding a pair side by side, arrived in the ink and answered in the lighter ink, heights
 *  relative to the period's busiest number (a zero day keeps its two bars at height 0), the day labels under the columns where they fit.
 *  Each column shows its tip in the kit's tooltip and is a keyboard stop spoken as it; the chart is a named group. */
export function FlowBars({ bars, label }: { bars: FlowBar[]; /** the chart's spoken name; by default the period's two sums */ label?: string }) {
  const top = Math.max(1, ...bars.map((b) => Math.max(b.arrived, b.answered)));
  const every = Math.ceil(bars.length / MOST_LABELS);
  const share = (v: number) => Math.round((v / top) * 100);
  const sum = (k: "arrived" | "answered") => bars.reduce((n, b) => n + b[k], 0);
  return (
    <div className="ob-bars ob-bars--pairs" role="group" aria-label={label ?? `${sum("arrived")} arrived, ${sum("answered")} answered`}>
      {bars.map((b, i) => (
        <div key={b.key} className="ob-bars__col" data-tip={b.tip} role="img" aria-label={b.tip} tabIndex={0}>
          <span className="ob-bars__pair">
            <span className="ob-bars__bar ob-bars__bar--arrived" style={{ ["--v" as string]: share(b.arrived) }} />
            <span className="ob-bars__bar ob-bars__bar--answered" style={{ ["--v" as string]: share(b.answered) }} />
          </span>
          <span className="ob-bars__label" aria-hidden="true">{(bars.length - 1 - i) % every === 0 ? b.label : ""}</span>
        </div>
      ))}
    </div>
  );
}

export type ActivityFact = { value: string; label: string };

export function ActivityCard({ open, change, bars, dates, facts, action }: {
  /** the team's open tickets now (the All open view's number) */ open: number;
  /** the sentence under the number: what the bars show in words */ change: string;
  bars: FlowBar[]; /** the period's dates, right of the legend: "Oct 21 – Nov 3" */ dates: string;
  /** the small figures, in order */ facts: ActivityFact[];
  /** optional: a link around or beside the open number (the screen's) */ action?: ReactNode;
}) {
  return (
    <div className="ob-activity">
      <div className="ob-activity__hero"><span className="ob-activity__value">{open}</span><span className="ob-activity__unit">{open === 1 ? "open ticket" : "open tickets"}</span>{action}</div>
      <p className="ob-activity__change">{change}</p>
      <div className="ob-activity__chart"><FlowBars bars={bars} /></div>
      <div className="ob-activity__legend">
        <span className="ob-activity__key ob-activity__key--arrived">Arrived</span>
        <span className="ob-activity__key ob-activity__key--answered">Answered</span>
        <span className="ob-activity__dates">{dates}</span>
      </div>
      <div className="ob-activity__facts">
        {facts.map((f) => <div key={f.label} className="ob-activity__fact"><span className="ob-activity__fact-value">{f.value}</span><span className="ob-activity__fact-label">{f.label}</span></div>)}
      </div>
    </div>
  );
}
